import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, Send, Shield, AlertTriangle } from "lucide-react";

export const CertificationSection = ({ profile, onSubmit, submitting }) => {
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = () => {
    if (!agreed || !signature.trim()) {
      return;
    }
    setShowConfirm(true);
  };

  const confirmSubmit = () => {
    onSubmit({
      agreed: true,
      signature: signature.trim(),
      timestamp: new Date().toISOString(),
      candidateName: profile?.fullName
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Before submitting your assessment, please review and certify your responses.
      </p>

      {/* Certification Statement */}
      <Card className="assessment-card border-[#003366]/20">
        <CardHeader>
          <CardTitle className="text-lg text-[#003366] flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Certification Statement
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-slate-50 rounded-lg p-4 border">
            <p className="text-sm leading-relaxed">
              I certify that the information provided in this self-assessment is accurate 
              and complete to the best of my knowledge. I understand this information will 
              be used for capability assessment and career development purposes.
            </p>
          </div>

          <div className="space-y-4">
            {/* Agreement Checkbox */}
            <div 
              className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                agreed ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setAgreed(!agreed)}
            >
              <Checkbox
                id="certification-agree"
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-0.5"
                data-testid="certification-checkbox"
              />
              <Label htmlFor="certification-agree" className="cursor-pointer flex-1">
                <span className="font-medium">I agree to the certification statement above</span>
                <p className="text-sm text-muted-foreground mt-1">
                  By checking this box, you confirm that all responses are accurate to the best of your knowledge.
                </p>
              </Label>
            </div>

            {/* Digital Signature */}
            <div className="space-y-2">
              <Label htmlFor="signature" className="text-sm font-medium">
                Digital Signature (Type your full name)
              </Label>
              <Input
                id="signature"
                type="text"
                placeholder="Enter your full name as signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                className="h-12"
                data-testid="signature-input"
              />
              <p className="text-xs text-muted-foreground">
                This serves as your digital signature for this assessment.
              </p>
            </div>

            {/* Date Display */}
            <div className="flex justify-between items-center py-3 border-t">
              <span className="text-sm text-muted-foreground">Date</span>
              <span className="font-medium">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="flex flex-col items-center gap-4 pt-6">
        <Button
          onClick={handleSubmit}
          disabled={!agreed || !signature.trim() || submitting}
          className="w-full sm:w-auto px-12 h-12 bg-[#003366] hover:bg-[#003366]/90 gap-2 shadow-lg shadow-[#003366]/20 text-lg"
          data-testid="submit-assessment-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Submit Assessment
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground text-center max-w-md">
          Once submitted, your responses will be reviewed by the Technology Capability Assessment team.
        </p>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full animate-fade-in">
            <CardHeader>
              <CardTitle className="text-lg text-[#003366] flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#B8860B]" />
                Confirm Submission
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to submit your assessment? This action cannot be undone.
              </p>
              <div className="bg-slate-50 rounded-lg p-3 border">
                <p className="text-sm">
                  <span className="text-muted-foreground">Signed by:</span>{" "}
                  <span className="font-medium">{signature}</span>
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirm(false)}
                  className="flex-1"
                  data-testid="cancel-submit-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmSubmit}
                  disabled={submitting}
                  className="flex-1 bg-[#003366] hover:bg-[#003366]/90"
                  data-testid="confirm-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Yes, Submit"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CertificationSection;
